import {Injectable} from '@angular/core';
import {ProviderPreview} from "../interfaces/provider-preview";
import {CompanyDetails} from "../interfaces/company-details";
import {ProfessionsService} from "./professions.service";
import {ProviderTypes} from "../enums/provider-types";
import {ImageService} from "../atoms/image/image.service";
import {ItemActionElement} from "../atoms/item-actions/item-action-element";
import {isEntityInFavorites} from "../store/selectors/user.selector";
import {Observable, Subject, take, takeWhile} from "rxjs";
import {toggleFavorite} from "../store/actions/user.actions";
import {NotificationService} from "../atoms/notification/notification.service";
import {Store} from "@ngrx/store";
import {AppState} from "../store";
import {CdColor} from "../enums/cd-color";
import {CheckboxFilterOption} from "../search-filter/checkbox-filter/checkbox-filter-option";
import {GalleryItemData} from "../shared/gallery-preview/gallery-item-data";
import {GalleryData} from "../shared/gallery-preview/gallery-data";
import {ProviderOffer} from "../interfaces/provider-offer";
import {ShareOverlayComponent} from "../shared/share-overlay/share-overlay.component";
import {OverlayService} from "../overlay/overlay.service";
import {resetFiltersByEntity, setJobFilter} from "../store/actions/filter.actions";
import {StoryService} from "./story.service";
import {FavoriteEntityType} from "../enums/favorite-entity-type";
import {GetGlobalSearchResults_globalSearch_profiles} from "../queries/__generated__/GetGlobalSearchResults";
import {GlobalSearchResult} from "../search-filter/global-search-overlay/global-search-result";
import {Image} from "../interfaces/image";
import {GlobalSearchResultType} from "../enums/global-search-result-type";
import {GetCompanyProfileById_companyProfile} from "../queries/__generated__/GetCompanyProfileById";
import {ServerVideoService} from "./server-video.service";
import {Document} from "../interfaces/document";
import {CompanyOfferItem} from "../interfaces/company-offer-item";
import {ProviderEventProfile} from "../interfaces/backend/provider-event-profile";
import {GET_COMPANY_SIZES} from "../queries/GET_COMPANY_SIZES";
import {ContactPerson} from "../interfaces/contact-person";
import {Location} from "../interfaces/location";
import {Benefit} from "../interfaces/benefit";
import {RawStory} from "./story.service";
import {CompanySize} from "../interfaces/company-size";
import {Address} from "../interfaces/backend/address";
import {PositionType} from "../enums/position-type";

// The Raw* interfaces below describe the common shape of the data passed to the parsers by the various
// queries (generated types and backend interfaces). Enum values are typed as string so the generated enum
// types fit as well, images and videos are passed to ImageService/ServerVideoService untouched.

// Backend delivers the id as Int, some outdated generated types still declare it as string
interface RawCompanyBenefit {
  id: number | string;
  name: string;
}

interface RawOffer {
  id?: string;
  offerType: string | null;
  name: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

interface RawLocation {
  id?: string | null;
  name: string | null;
  address: {
    id?: string;
    street: string | null;
    number?: string | null;
    addressCity: {
      name: string;
      addressDistrict?: {
        name: string;
      } | null;
    } | null;
    addressLatLon?: {
      lat: string | null;
      lon: string | null;
    } | null;
  } | null;
}

// Not every query fetches id, visible and position (and the generated type of GET_EVENT_BY_ID is outdated)
interface RawMediaGalleryItem {
  id?: string;
  contentType: string | null;
  visible?: boolean;
  position?: number;
  image?: unknown;
  video?: unknown;
}

interface RawMediaGallery {
  name?: string | null;
  galleryItems: (RawMediaGalleryItem | null)[] | null;
}

// id and enabled are missing in some outdated generated types, though queried by the contactGalleryFields fragment
interface RawContactGallery {
  galleryEntries: {
    id?: string;
    enabled?: boolean;
    contact: {
      employeePosition: string | null;
      picture: unknown;
      communication: {
        entries: {
          entryValue: string;
          communicationType: {
            shortName: string;
          };
        }[] | null;
      };
      person: {
        firstname: string;
        lastname: string;
      };
    };
  }[] | null;
}

// GET_PROFILES_SEARCH_RESULTS (the generated type is outdated and misses quizDone)
interface RawProviderPreview {
  id: string;
  uuid: string;
  name: string;
  profileType: {
    id: string;
  } | null;
  benefits: {
    id?: number;
    name: string;
  }[] | null;
  picture: unknown;
  jobs: {
    name: string;
    interestFields: {
      id: string;
    }[] | null;
  }[] | null;
  address: {
    id?: string;
    street: string | null;
    addressCity: {
      name: string;
      addressDistrict: {
        name: string;
      } | null;
    } | null;
    addressLatLon: {
      lat: string | null;
      lon: string | null;
    } | null;
  }[] | null;
  hasStory: boolean | null;
  quizDone?: boolean;
}

interface RawProfileJob {
  interestFields: {
    id: string;
  }[] | null;
}

interface RawRelatedProfile {
  id: string;
  uuid: string;
  name: string;
  jobs: {
    name: string;
    interestFields: {
      id: string;
    }[] | null;
  }[] | null;
  locations: RawLocation[] | null;
  hasStory?: boolean | null;
  companyBenefits?: RawCompanyBenefit[] | null;
  companyProfilePicture?: unknown;
  schoolProfilePicture?: unknown;
  boPartnerProfilePicture?: unknown;
}

// Fields shared by the company, school and BO partner profile queries (public and editor variant)
interface RawProviderDetails {
  id: string;
  uuid: string;
  name: string;
  description: string | null;
  quizUrl: string | null;
  logoStyle?: string | null;
  headerPicture: unknown;
  locations: RawLocation[] | null;
  jobs: RawProfileJob[] | null;
  url: string | null;
  contactGallery: RawContactGallery | null;
  profileLink: {
    entries: {
      linkType: string;
      linkValue: string;
    }[] | null;
  } | null;
  mediaGallery: RawMediaGallery | null;
  documents: RawDocumentGallery | null;
  offers: RawOffer[] | null;
  trainingInformation?: string | null;
  certificateInformation?: string | null;
}

// GET_COMPANY_PROFILE_BY_ID, GET_SELECTED_COMPANY_PROFILE
interface RawCompanyDetails extends RawProviderDetails {
  founded: number | null;
  companyProfilePicture: unknown;
  companySize: {
    id: string;
    name: string;
    text: string;
  } | null;
  certificates: {
    id: string;
    name: string;
    picturePath: string;
  }[] | null;
  companyBenefits: RawCompanyBenefit[] | null;
  jobMarkets: {
    id: string;
    name: string;
    positionType: string | null;
    addresses: {
      addressCity: {
        name: string;
      } | null;
    }[] | null;
    jobs: RawProfileJob[] | null;
  }[] | null;
  relatedCompanies: RawRelatedProfile[] | null;
}

// GET_SCHOOL_PROFILE_BY_ID, GET_SELECTED_SCHOOL_PROFILE (only the public query fetches stories)
interface RawSchoolDetails extends RawProviderDetails {
  schoolProfilePicture: unknown;
  relatedSchools: RawRelatedProfile[] | null;
  stories?: RawStory[] | null;
  videoUrl: string | null;
}

// GET_BOINSTITUTION_PROFILE_BY_ID, GET_SELECTED_BO_PARTNER_PROFILE
interface RawBoInstitutionDetails extends RawProviderDetails {
  boPartnerProfilePicture: unknown;
  relatedBoPartners: RawRelatedProfile[] | null;
}

interface RawDocumentGallery {
  galleryItems: ({
    id?: string | null;
    document: {
      name: string | null;
      filePath: string | null;
    } | null;
  } | null)[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class ProvidersService {
  private readonly PROVIDER_SINGLE_PAGE_ENDPOINT = "anbieter";
  private readonly PROVIDERS_OVERVIEW_PAGE_ENDPOINT = "anbieter";
  public readonly PROVIDERS_HASH_FILTER_PREFIX = "providersFilter";
  private _providersPreviews: ProviderPreview[] = [];
  private loadingState = 0; // 0 not loading, 1 loading, 2 loaded
  private _providersPreviewSubject = new Subject<ProviderPreview[]>();
  private _providerOffersFilter: CheckboxFilterOption[] = [];

  constructor(
    private professionsService: ProfessionsService,
    private image: ImageService,
    private notifications: NotificationService,
    private store: Store<AppState>,
    private overlay: OverlayService,
    private storyService: StoryService,
    private videoService: ServerVideoService
  ) { }

  /** Deprecated */
  getProvidersPreviews(): Promise<ProviderPreview[]> {
    return new Promise<ProviderPreview[]>((resolve, reject) => {
      if(this._providersPreviews.length) {
        resolve(this._providersPreviews);
      } else {
        if(this.loadingState === 0) {
          this._providersPreviewSubject.pipe(takeWhile((el) => el.length === 0, true)).subscribe((previews) => {
            resolve(previews);
          });
        }
      }
    })
  }

  companyDetailsToProviderPreview(companyDetails: CompanyDetails): ProviderPreview {
    return {
      benefits: companyDetails.benefits,
      hasStories: companyDetails.stories.length > 0,
      id: companyDetails.id,
      image: companyDetails.image,
      jobs: companyDetails.jobMarkets,
      locations: companyDetails.locations,
      name: companyDetails.name,
      providerType: companyDetails.providerType,
      uuid: companyDetails.uuid,
      interestFields: []
    }
  }
  getFavoriteEntityType(providerType: ProviderTypes): FavoriteEntityType {
    let favoriteEntityType = FavoriteEntityType.PROVIDER_SCHOOL;
    if(providerType === ProviderTypes.COMPANY) favoriteEntityType = FavoriteEntityType.PROVIDER_COMPANY;
    else if(providerType === ProviderTypes.BOINSTITUTION) favoriteEntityType = FavoriteEntityType.PROVIDER_INSTITUTION;
    return favoriteEntityType;
  }

  mapRawProviderPreview(rawProviderData: RawProviderPreview): ProviderPreview {
    let providerType = 0;

    switch (rawProviderData?.profileType?.id) {
      case "1":
        providerType = ProviderTypes.COMPANY;
        break;
      case "2":
        providerType = ProviderTypes.BOINSTITUTION;
        break;
      case "3":
        providerType = ProviderTypes.SCHOOL;
        break;
      default:
        providerType = ProviderTypes.COMPANY;
    }

    const companyDetails: ProviderPreview = {
      providerType,
      id: parseInt(rawProviderData.id),
      uuid: rawProviderData.uuid,
      name: rawProviderData.name,
      image: this.image.mapRawImageData(rawProviderData.picture),
      benefits: new Array(0),
      locations: new Array(0),
      jobs: new Array(0),
      hasStories: rawProviderData.hasStory ?? false,
      interestFields: [],
      quizDone: rawProviderData.quizDone ?? false
    }

    rawProviderData.address?.forEach((address) => {
      companyDetails.locations.push({
        name: address.addressCity?.name ?? "",
        address: {
          // not queried in GET_PROFILES_SEARCH_RESULTS (undefined), kept as is
          id: address.id as unknown as number,
          street: address.street ?? '',
          city: address.addressCity?.name ?? '',
          district: address.addressCity?.addressDistrict?.name ?? '',
          lat: parseFloat(address.addressLatLon?.lat ?? "0"),
          lng: parseFloat(address.addressLatLon?.lon ?? "0")
        }
      })
    });

    rawProviderData.benefits?.forEach((companyBenefit) => {
      companyDetails.benefits.push({
        // not queried in GET_PROFILES_SEARCH_RESULTS (undefined), kept as is
        id: companyBenefit.id as number,
        name: companyBenefit.name
      })
    });

    rawProviderData.jobs?.forEach((job) => {
      companyDetails.jobs.push({
        name: job.name
      });

      if(job.interestFields && job.interestFields.length) {
        job.interestFields.forEach((interestField) => {
          companyDetails.interestFields = this.pushUniqueNumber(companyDetails.interestFields, parseInt(interestField.id));
        })
      }
    });

    return companyDetails;
  }

  mapRawCompanyDetailsData(rawProviderData: RawCompanyDetails): CompanyDetails {

    let companyDetails: CompanyDetails = {
      id: parseInt(rawProviderData.id),
      uuid: rawProviderData.uuid ?? "",
      providerType: ProviderTypes.COMPANY,
      name: rawProviderData.name,
      description: rawProviderData.description ?? "",
      // kept as delivered ("" if missing), CompanySize expects a numeric id
      companySize: (rawProviderData.companySize ?? "") as unknown as CompanySize,
      professions: [],
      certificates: [],
      founded: rawProviderData.founded ?? 0,
      locations: this.parseLocations(rawProviderData?.locations, rawProviderData?.name),
      benefits: this.parseCompanyBenefits(rawProviderData.companyBenefits),
      trainingInformation: rawProviderData.trainingInformation ?? '',
      certificateInformation: rawProviderData.certificateInformation ?? '',
      url: rawProviderData.url ?? '',
      contactPersons: this.parseContactGallery(rawProviderData.contactGallery),
      documents: this.parseDocuments(rawProviderData.documents),
      offers: this.mapRowOffers(rawProviderData),
      image: this.image.mapRawImageData(rawProviderData.companyProfilePicture),
      headerImage: rawProviderData?.headerPicture ? this.image.mapRawImageData(rawProviderData.headerPicture) : undefined,
      links: [],
      mediaGallery: this.parseMediaGallery(rawProviderData.mediaGallery),
      relatedCompanies: [],
      stories: [],
      jobMarkets: [],
      videoUrl: "",
      interestFields: [],
      quizUrl: rawProviderData.quizUrl ?? "",
      logoStyle: rawProviderData.logoStyle ?? "NORMAL"
    }

    if(rawProviderData.jobMarkets?.length) {
      rawProviderData.jobMarkets?.forEach((rawJobMarket) => {

        // const professionPreview: ProfessionPreview = this.professionsService.mapRawProfessionPreviewData(rawJobMarket.job)

        // id, positionType and addresses are kept as delivered (string id, string literal, partial addresses)
        const parsedJobMarket: CompanyDetails["jobMarkets"][number] = {
          id: (rawJobMarket.id ?? -1) as unknown as number,
          name: rawJobMarket.name ?? "",
          positionType: rawJobMarket.positionType as PositionType,
          addresses: rawJobMarket.addresses as unknown as Address[],
          matchingScore: 1,
          interestFields: Array(0)
          // matchingScore: this.professionUserMatchingService.getJobMatchingScore(professionPreview)
        }

        if(rawJobMarket.jobs && rawJobMarket.jobs.length) {
          rawJobMarket.jobs.forEach((profession) => {
            if(profession.interestFields && profession.interestFields.length) {
              profession.interestFields.forEach((interestField) => {
                parsedJobMarket.interestFields = this.pushUniqueNumber(parsedJobMarket.interestFields, parseInt(interestField.id));
              })
            }
          })
        }

        companyDetails.jobMarkets.push(parsedJobMarket);

      })
    }

    if(rawProviderData?.relatedCompanies?.length) {
      rawProviderData.relatedCompanies.forEach((rawRelatedCompany) => {
        const parsedRelatedCompany: ProviderPreview = {
          providerType: ProviderTypes.COMPANY,
          id: parseInt(rawRelatedCompany.id),
          uuid: rawRelatedCompany.uuid,
          name: rawRelatedCompany.name,
          image: this.image.mapRawImageData(rawRelatedCompany.companyProfilePicture),
          benefits: this.parseCompanyBenefits(rawRelatedCompany.companyBenefits),
          locations: this.parseLocations(rawRelatedCompany.locations, rawProviderData.name),
          jobs: new Array(0),
          hasStories: rawRelatedCompany.hasStory ?? false,
          interestFields: []
        };

        rawRelatedCompany.jobs?.forEach((job) => {
          parsedRelatedCompany.jobs.push({
            name: job.name
          });

          if(job.interestFields && job.interestFields.length) {
            job.interestFields.forEach((interestField) => {
              parsedRelatedCompany.interestFields = this.pushUniqueNumber(parsedRelatedCompany.interestFields, parseInt(interestField.id));
            })
          }
        });

        companyDetails.relatedCompanies.push(parsedRelatedCompany);

      });
    }

    if(rawProviderData?.jobs?.length) {
      rawProviderData.jobs.forEach((job) => {
        companyDetails.professions.push(this.professionsService.mapRawProfessionPreviewData(job));
        if(job.interestFields && job.interestFields.length) {
          job.interestFields.forEach((interestField) => {
            companyDetails.interestFields = this.pushUniqueNumber(companyDetails.interestFields, parseInt(interestField.id));
          })
        }
      });
    }

    if(rawProviderData?.certificates?.length) {
      rawProviderData.certificates.forEach((certificate) => {
        companyDetails.certificates.push({
          id: parseInt(certificate.id),
          name: certificate.name,
          picturePath: certificate.picturePath
        });
      });
    }

    if(rawProviderData?.profileLink?.entries?.length) {
      rawProviderData.profileLink.entries.forEach((entryLink) => {
        companyDetails.links.push({
          type: entryLink.linkType ?? "",
          value: entryLink.linkValue ?? ""
        })
      });
    }

    return companyDetails;

  }


  mapRawSchoolDetailsData(rawProviderData: RawSchoolDetails): CompanyDetails {

    let companyDetails: CompanyDetails = {
      id: parseInt(rawProviderData.id),
      uuid: rawProviderData.uuid,
      providerType: ProviderTypes.SCHOOL,
      name: rawProviderData.name,
      description: rawProviderData.description as string,
      // schools have no company size (undefined), kept as is
      companySize: undefined as unknown as CompanySize,
      professions: [],
      certificates: [],
      founded: -1,
      locations: this.parseLocations(rawProviderData.locations, rawProviderData.name),
      benefits: [],
      trainingInformation: rawProviderData.trainingInformation ?? '',
      certificateInformation: rawProviderData.certificateInformation ?? '',
      url: rawProviderData.url ?? '',
      contactPersons: this.parseContactGallery(rawProviderData.contactGallery),
      documents: this.parseDocuments(rawProviderData.documents),
      offers: this.mapRowOffers(rawProviderData),
      image: this.image.mapRawImageData(rawProviderData.schoolProfilePicture),
      headerImage: rawProviderData.headerPicture ? this.image.mapRawImageData(rawProviderData.headerPicture) : undefined,
      links: [],
      mediaGallery: this.parseMediaGallery(rawProviderData.mediaGallery),
      relatedCompanies: [],
      stories: [],
      jobMarkets: [],
      videoUrl: rawProviderData.videoUrl ?? "",
      interestFields: [],
      quizUrl: rawProviderData.quizUrl ?? "",
      logoStyle: rawProviderData.logoStyle ?? "NORMAL"
    }

    if(rawProviderData.stories && rawProviderData.stories.length) {
      companyDetails.stories = rawProviderData.stories.map((story) => this.storyService.mapRawStoryData(story));
    }

    if(rawProviderData.relatedSchools?.length) {
      rawProviderData.relatedSchools.forEach((rawRelatedCompany) => {
        const parsedRelatedCompany: ProviderPreview = {
          providerType: ProviderTypes.SCHOOL,
          id: parseInt(rawRelatedCompany.id),
          uuid: rawRelatedCompany.uuid,
          name: rawRelatedCompany.name,
          image: this.image.mapRawImageData(rawRelatedCompany.schoolProfilePicture),
          benefits: [],
          locations: this.parseLocations(rawRelatedCompany.locations, rawRelatedCompany.name),
          jobs: new Array(0),
          hasStories: rawRelatedCompany.hasStory ?? false,
          interestFields: []
        };

        rawRelatedCompany.jobs?.forEach((job) => {
          parsedRelatedCompany.jobs.push({
            name: job.name
          });

          if(job.interestFields && job.interestFields.length) {
            job.interestFields.forEach((interestField) => {
              parsedRelatedCompany.interestFields = this.pushUniqueNumber(parsedRelatedCompany.interestFields, parseInt(interestField.id));
            })
          }
        });

        companyDetails.relatedCompanies.push(parsedRelatedCompany);

      });
    }

    rawProviderData.jobs?.forEach((job) => {
      companyDetails.professions.push(this.professionsService.mapRawProfessionPreviewData(job));

      if(job.interestFields && job.interestFields.length) {
        job.interestFields.forEach((interestField) => {
          companyDetails.interestFields = this.pushUniqueNumber(companyDetails.interestFields, parseInt(interestField.id));
        })
      }
    });

    if(rawProviderData?.profileLink?.entries?.length) {
      rawProviderData.profileLink.entries.forEach((entryLink) => {
        companyDetails.links.push({
          type: entryLink.linkType ?? "",
          value: entryLink.linkValue ?? ""
        })
      });
    }

    return companyDetails;

  }

  mapRawBoInstitutionDetailsData(rawProviderData: RawBoInstitutionDetails): CompanyDetails {

    let companyDetails: CompanyDetails = {
      id: parseInt(rawProviderData.id),
      uuid: rawProviderData.uuid,
      providerType: ProviderTypes.BOINSTITUTION,
      name: rawProviderData.name,
      description: rawProviderData.description as string,
      // BO partners have no company size (undefined), kept as is
      companySize: undefined as unknown as CompanySize,
      professions: [],
      certificates: [],
      founded: -1,
      locations: this.parseLocations(rawProviderData.locations, rawProviderData.name),
      benefits: [],
      trainingInformation: '',
      certificateInformation: rawProviderData.certificateInformation ?? '',
      url: rawProviderData.url ?? '',
      contactPersons: this.parseContactGallery(rawProviderData.contactGallery),
      documents: this.parseDocuments(rawProviderData.documents),
      offers: this.mapRowOffers(rawProviderData),
      image: this.image.mapRawImageData(rawProviderData.boPartnerProfilePicture),
      headerImage: rawProviderData.headerPicture ? this.image.mapRawImageData(rawProviderData.headerPicture) : undefined,
      links: [],
      mediaGallery: this.parseMediaGallery(rawProviderData.mediaGallery),
      relatedCompanies: [],
      stories: [],
      jobMarkets: [],
      videoUrl: "",
      interestFields: [],
      quizUrl: rawProviderData.quizUrl ?? "",
      logoStyle: rawProviderData.logoStyle ?? "NORMAL"
    }

    if(rawProviderData.relatedBoPartners?.length) {
      rawProviderData.relatedBoPartners.forEach((rawRelatedCompany) => {
        const parsedRelatedCompany: ProviderPreview = {
          providerType: ProviderTypes.BOINSTITUTION,
          id: parseInt(rawRelatedCompany.id),
          uuid: rawRelatedCompany.uuid,
          name: rawRelatedCompany.name,
          image: this.image.mapRawImageData(rawRelatedCompany.boPartnerProfilePicture),
          benefits: [],
          locations: this.parseLocations(rawRelatedCompany.locations, rawRelatedCompany.name),
          jobs: new Array(0),
          hasStories: rawRelatedCompany.hasStory ?? false,
          interestFields: []
        };

        rawRelatedCompany.jobs?.forEach((job) => {
          parsedRelatedCompany.jobs.push({
            name: job.name
          })

          if(job.interestFields && job.interestFields.length) {
            job.interestFields.forEach((interestField) => {
              parsedRelatedCompany.interestFields = this.pushUniqueNumber(parsedRelatedCompany.interestFields, parseInt(interestField.id));
            })
          }
        });

        companyDetails.relatedCompanies.push(parsedRelatedCompany);

      });
    }

    rawProviderData.jobs?.forEach((job) => {
      companyDetails.professions.push(this.professionsService.mapRawProfessionPreviewData(job));

      if(job.interestFields && job.interestFields.length) {
        job.interestFields.forEach((interestField) => {
          companyDetails.interestFields = this.pushUniqueNumber(companyDetails.interestFields, parseInt(interestField.id));
        })
      }
    });

    if(rawProviderData?.profileLink?.entries?.length) {
      rawProviderData.profileLink.entries.forEach((entryLink) => {
        companyDetails.links.push({
          type: entryLink.linkType ?? "",
          value: entryLink.linkValue ?? ""
        })
      });
    }

    return companyDetails;

  }

  private pushUniqueNumber(arr: number[], id: number) {
    if(!arr.includes(id)) {
      arr.push(id);
    }
    return arr;
  }

  getFullUrl(uuid: string, type: ProviderTypes): string {
    return `${window.location.origin}/${this.PROVIDER_SINGLE_PAGE_ENDPOINT}/${this.getProviderEndpointByType(type)}/${uuid}`;
  }

  getProviderEndpointByType(type: ProviderTypes): string {
    if(type === ProviderTypes.BOINSTITUTION) return "bo-einrichtung";
    if(type === ProviderTypes.COMPANY) return "unternehmen";
    if(type === ProviderTypes.SCHOOL) return "schule";
    return "unternehmen";
  }

  getProviderDetailPageUrlById(uuid: string, type: ProviderTypes): string {
    return `/${this.PROVIDER_SINGLE_PAGE_ENDPOINT}/${this.getProviderEndpointByType(type)}/${uuid}`;
  }
  async generateMenuItems(provider: ProviderPreview): Promise<ItemActionElement[]> {
    const isFavorite = await new Promise<boolean>((resolve, reject) => {
      this.store.select(isEntityInFavorites(provider.uuid)).pipe(take(1)).subscribe((value) => {
        resolve(value);
      });
    });
    const menuItems: ItemActionElement[] = [
      {
        func: () => {
          this.store.dispatch(toggleFavorite({entityType: this.getFavoriteEntityType(provider.providerType), id: provider.uuid}));

          if(!isFavorite) {
            this.notifications.push("Gemerkt", CdColor.RIVER, "check-circle");
          } else {
            this.notifications.push("Aus Merkliste entfernt", CdColor.RIVER, "check-circle");
          }
        },
        title: isFavorite ? 'Gemerkt' : 'Merken',
        icon: isFavorite ? 'bookmark-full' : 'bookmark-outline',
        isActive: true
      },
      {
        func: () => {
          window.navigator.clipboard.writeText(this.getFullUrl(provider.uuid, provider.providerType));
          this.notifications.push("Link kopiert", CdColor.RIVER, 'check-circle');
        },
        title: "Link kopieren",
        icon: "link",
        isActive: true
      },
      {
        func: () => {
          this.overlay.openWindowOverlay({componentMain: {component: ShareOverlayComponent, data: {url: this.getFullUrl(provider.uuid, provider.providerType)}}, title: "Teilen " + provider.name})
        },
        title: 'Teilen',
        icon: "share",
        isActive: true
      }
    ];

    return menuItems;
  }

  mapRowOffers(rawProviderData: {offers: RawOffer[] | null}): ProviderOffer[] {
    let parsedOffers: ProviderOffer[] = [];

    if(rawProviderData.offers) {
      rawProviderData.offers.forEach((rawOffer) => {

        let iconFile = "SUBEJCT=UA Betriebserkundungen.svg";

        switch (rawOffer.offerType) {
          case "WORKSHOP":
            iconFile = "SUBEJCT=BO Workshops.svg";
            break;
          case "BERUFSPRAKTISCH":
            iconFile = "SUBEJCT=UA Berufspraktische Tage.svg";
            break;
          case "UNTERNEHMENSCHULE":
            iconFile = "SUBEJCT=UA Vorstellung an Schule.svg";
            break;
          case "VORTRAG":
            iconFile = "SUBEJCT=BO Vortrag.svg";
            break;
          case "BERUFSBERATUNG":
            iconFile = "SUBEJCT=BO Berufsberatung.svg";
            break;
          case "TAGOFFENETUER":
            iconFile = "SUBEJCT=Schule Offene Tuer.svg";
            break;
        }

        parsedOffers.push({
          // id is not queried everywhere (NaN then), kept as is
          id: parseInt(rawOffer.id as string) ?? -1,
          icon: iconFile,
          // kept as delivered, ProviderOffer is stricter than the backend data
          label: rawOffer.name as string,
          dateFrom: rawOffer.dateFrom as string,
          dateTo: rawOffer.dateTo as string
        })
      });
    }

    return parsedOffers;

  }

  getBaseOverviewUrl(): string {
    return this.PROVIDERS_OVERVIEW_PAGE_ENDPOINT;
  }

  getOverviewUrlByPageIdx(idx: number): string {
    return `${this.getBaseOverviewUrl()}${idx === 1 ? '' : '/seite/' + idx}`;
  }

  /** Result looks like /berufe/page/3#asdsA234dfDa */
  /** Result looks like /berufe#asdsA234dfDa */
  /** Result looks like /berufe */
  /** Result looks like /berufe/page/3 */
  getFilterUrl(pageIdx: number, providerTypes: number[], interestFields: number[], providerOffers: number[], searchTerm: string, districtIDs: number[], locationTerm: string, distance: number, sortBy: string, withQuiz: boolean, completed: boolean, relatedProfessionBicId: number, userLocation: {lat: number, lng: number}): string {

    if(distance < 0) distance = 0;

    let baseUrl = this.getOverviewUrlByPageIdx(pageIdx);
    let queryString = "";
    if(providerTypes.length) {
      queryString = `${queryString}selectedProviderTypes=${providerTypes.join(",")}&`;
    }

    if(interestFields.length) {
      queryString = `${queryString}selectedProviderInterestFields=${interestFields.join(",")}&`;
    }

    if(providerOffers.length) {
      queryString = `${queryString}selectedProviderOffers=${providerOffers.join(",")}&`;
    }

    if(sortBy.length && sortBy !== "name-asc") { // like skip default sorting
      queryString = `${queryString}providersArchiveSortBy=${sortBy}&`;
    }

    if(withQuiz) {
      queryString = `${queryString}providersWithQuiz=1&`;
    }

    if(completed) {
      queryString = `${queryString}providersComplete=1&`;
    }

    if(searchTerm.length) {
      queryString = `${queryString}searchTermCompany=${searchTerm}&`;
    }

    if(districtIDs.length) {
      queryString = `${queryString}activeProviderDistrictIds=${districtIDs.join(",")}&`;
    }

    if(locationTerm.length) {
      queryString = `${queryString}searchTermProviderLocation=${locationTerm}&`;
    }

    if(distance) {
      queryString = `${queryString}providersDistance=${distance}&`;
    }

    if(relatedProfessionBicId !== -1) {
      queryString = `${queryString}relatedProviderProfessionBicId=${relatedProfessionBicId}&`;
    }

    if(userLocation.lat && userLocation.lng) {
      queryString = `${queryString}userLocationProvider=${userLocation.lat},${userLocation.lng}&`;
    }

    if(queryString.length) {
      // console.log(queryString);
      queryString = `${this.PROVIDERS_HASH_FILTER_PREFIX}:${queryString}`;
      queryString = btoa(queryString);
      baseUrl = `${baseUrl}#${queryString}`;
    }


    // console.log(baseUrl);

    return baseUrl;
  }

  setFiltersFromUrl(): void {

    let hash = window.location.hash.slice(1);
    if(hash.length) {
      this.store.dispatch(resetFiltersByEntity({key: 'allProvider'}));
      try {
        hash = atob(hash);
        const splitHash = hash.split(":");
        splitHash.shift();
        hash = splitHash.join("");
        const splitByFilters = hash.split("&");
        // console.log(splitByFilters);
        splitByFilters.forEach((filter) => {
          if(filter.length) {
            const splitFilter = filter.split("=");
            if(splitFilter.length === 2) {
              const filterName = splitFilter[0];
              switch (filterName) {
                // array integers type
                case "selectedProviderTypes": case "activeProviderDistrictIds": case "selectedProviderInterestFields": case "selectedProviderOffers":
                  const splitValues = splitFilter[1].split(",");
                  this.store.dispatch(setJobFilter({key: filterName, value: splitValues.map((value) => parseInt(value))}));
                  break;
                // single string value type
                case "searchTermCompany": case "providersArchiveSortBy": case "searchTermProviderLocation":
                  console.warn(splitFilter[1]);
                  this.store.dispatch(setJobFilter({key: filterName, value: splitFilter[1]}));
                  break;
                // integer type
                case "providersDistance": case "relatedProviderProfessionBicId":
                  const intValue = parseInt(splitFilter[1]);
                  if(!isNaN(intValue)) {
                    this.store.dispatch(setJobFilter({key: filterName, value: intValue}));
                  }
                  break;
                // true/false type
                case "providersWithQuiz": case "providersComplete":
                  const boolValue = splitFilter[1] !== '0';
                  this.store.dispatch(setJobFilter({key: filterName, value: boolValue}));
                  break;
                case "userLocationProvider":
                  let strLocation = splitFilter[1];
                  if(strLocation.length > 3 && strLocation.indexOf(",") !== -1) {
                    let arrLocation = strLocation.split(',');
                    if(arrLocation.length === 2) {
                      this.store.dispatch(setJobFilter({key: filterName, value: {lat: parseFloat(arrLocation[0]), lng: parseFloat(arrLocation[1])}}));
                    }
                  }
                  break;
              }
            }
          }

        })
      } catch (err: any) {
        // console.log(err);
      }
    }
  }

  parseLocations(rawLocations: RawLocation[] | null | undefined, mainLocationName: string): Location[] {

    if(!rawLocations) return [];

    let parsedLocations: Location[] = new Array(0);
    rawLocations.forEach((location) => {
      parsedLocations.push({
        id: parseInt(location.id ?? "-1"),
        name: location.name ?? mainLocationName,
        address: {
          // backend delivers the address id as string, kept as is
          id: (location.address?.id ?? -1) as number,
          street: ((location.address?.street ?? '') + ' ' + (location.address?.number ?? '')).trim(),
          city: location.address?.addressCity?.name ?? '',
          district: location.address?.addressCity?.addressDistrict?.name ?? '',
          lat: parseFloat(location.address?.addressLatLon?.lat ?? "0"),
          lng: parseFloat(location.address?.addressLatLon?.lon ?? "0")
        }
      })
    });

    return parsedLocations;

  }
  parseCompanyBenefits(rawCompanyBenefits: RawCompanyBenefit[] | null | undefined): Benefit[] {
    let parsedCompanyBenefits: Benefit[] = new Array(0);
    rawCompanyBenefits?.forEach((companyBenefit) => {
      parsedCompanyBenefits.push({
        // number at runtime (Int in the backend schema)
        id: companyBenefit.id as number,
        name: companyBenefit.name
      })
    });

    return parsedCompanyBenefits;

  }

  parseMediaGallery(rawMediaGallery: RawMediaGallery | null | undefined): GalleryData {

    // console.log(rawMediaGallery);
    const parsedMediaGallery: GalleryData = {
      title: rawMediaGallery?.name ?? "",
      items: []
    }

    rawMediaGallery?.galleryItems?.forEach((rawGalleryItem) => {
      if (rawGalleryItem?.contentType === "IMAGE") {
        const galleryItem: GalleryItemData = {
          id: parseInt(rawGalleryItem.id ?? "-1"),
          type: "image",
          image: this.image.mapRawImageData(rawGalleryItem.image),
          selected: rawGalleryItem.visible ?? true,
          position: rawGalleryItem.position ?? -1
        }
        parsedMediaGallery.items.push(galleryItem);
      } else if (rawGalleryItem?.contentType === "VIDEO") {
        const galleryItem: GalleryItemData = {
          id: parseInt(rawGalleryItem.id ?? "-1"),
          type: "video",
          video: this.videoService.mapRawVideoData(rawGalleryItem.video),
          selected: rawGalleryItem.visible ?? true,
          position: rawGalleryItem.position ?? -1
        }
        parsedMediaGallery.items.push(galleryItem);
      }
    });

    parsedMediaGallery.items = parsedMediaGallery.items.sort((a, b) => {
      const aPos = a.position ?? -1;
      const bPos = b.position ?? -1;
      return aPos > bPos ? 1 : -1;
    });

    return parsedMediaGallery;
  }

  parseContactGallery(rawContactGallery: RawContactGallery | null | undefined): ContactPerson[] {
    let parsedContactGallery: ContactPerson[] = new Array(0);
    rawContactGallery?.galleryEntries?.forEach((galleryEntity) => {
      parsedContactGallery.push({
        id: parseInt(galleryEntity.id as string) ?? -1,
        avatar: this.image.mapRawImageData(galleryEntity.contact.picture, "person"),
        firstName: galleryEntity.contact.person.firstname,
        lastName: galleryEntity.contact.person.lastname,
        position: galleryEntity.contact.employeePosition ?? "",
        enabled: galleryEntity.enabled ?? true,
        // undefined if there are no entries, kept as is
        communicationEntries: galleryEntity.contact?.communication?.entries?.map((communicationEntry) => {
          return {
            value: communicationEntry.entryValue,
            type: communicationEntry.communicationType.shortName
          }
        }) as ContactPerson["communicationEntries"]
      })
    });
    return parsedContactGallery;
  }

  parseDocuments(rawDocuments: RawDocumentGallery | null | undefined): Document[] {

    if(!rawDocuments) return new Array(0);


    let parsedDocuments: Document[] = new Array(0);
    // console.log(rawDocuments);
    rawDocuments?.galleryItems?.forEach((rawGalleryItem) => {
      parsedDocuments.push({
        name: rawGalleryItem?.document?.name ?? "",
        filePath: rawGalleryItem?.document?.filePath ?? "",
        // backend delivers the id as string, kept as is
        id: (rawGalleryItem?.id ?? -1) as number,
      })
    });

    return parsedDocuments;

  }

  mapGlobalSearchResult(rawData: GetGlobalSearchResults_globalSearch_profiles): GlobalSearchResult {
    let type: GlobalSearchResultType = GlobalSearchResultType.SCHOOL;
    switch (rawData.profileType.id) {
      case "1":
        type = GlobalSearchResultType.COMPANY;
        break;
      case "2":
        type = GlobalSearchResultType.INSTITUTION;
        break;
      case "3":
        type = GlobalSearchResultType.SCHOOL;
        break;
    }
    return {
      id: rawData.uuid,
      image: rawData.picture as unknown as Image,
      quote: "",
      title: rawData.name ?? "",
      type: type,
      isGreen: false
    }
  }

  parseProviderOffers(rawProviderOffers: ProviderEventProfile[] | null): CompanyOfferItem[] {

    let providerOffers: CompanyOfferItem[] = [];

    if(rawProviderOffers) {
      rawProviderOffers.forEach((provider) => {
        let parsedProvider: CompanyOfferItem = {
          image: this.image.mapRawImageData(provider.headerPicture),
          id: parseInt(provider.id),
          name: provider.name,
          offers: [],
          mailto: "",
          providerUrl: provider.providerUrl
        };

        if(provider.offers) {
          parsedProvider.offers = this.mapRowOffers(provider);
        }

        if(provider.contactGallery) {
          (provider.contactGallery.galleryEntries ?? []).forEach((contact) => {
            if (contact.contact.communication.entries) {
              contact.contact.communication.entries.forEach((entry) => {
                if (entry.communicationType.shortName === "MAILB") {
                  parsedProvider.mailto = entry.entryValue;
                }
              });
            }
          })
        }

        providerOffers.push(parsedProvider)
      });
    }

    return providerOffers;

  }

}
